import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import api, { API_URL } from '../services/api';
import { USER_PROFILE, AUTH_LOGOUT, AUTH_LOGOUT_OTHER_SESSIONS, AUTH_LOGIN, AUTH_LOGIN_REQUEST_OTP, AUTH_LOGIN_VERIFY_OTP, CLEAR_USER_SESSIONS, SESSION_CHECK } from '../config/Config';

// Create the auth context
// Security note: Prefer HttpOnly cookie-based auth tokens (set by backend on login) over
// storing tokens in localStorage to reduce XSS token theft risk. Current flow uses Token in headers.
const AuthContext = createContext(null);

// Create a clean axios instance for login (no CSRF interceptors)
// withCredentials: false - do NOT send cookies; avoids 403 when cookies are corrupt after browser close
// backend auth endpoints use csrf_exempt so they work without cookies
const cleanAxios = axios.create({
  baseURL: API_URL,
  withCredentials: false,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
    'X-Requested-With': 'XMLHttpRequest',
  },
  validateStatus: function (status) {
    return status < 500;
  }
});

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
  const isInitializingRef = useRef(false);
  const navigate = useNavigate();

  // Snackbar functions
  const showSnackbar = useCallback((message, severity = 'info') => {
    setSnackbar({ open: true, message, severity });
  }, []);

  const handleCloseSnackbar = useCallback(() => {
    setSnackbar(prev => ({ ...prev, open: false }));
  }, []);

  // Process user data from API response
  const processUserData = useCallback((data) => {
    if (!data) return null;
    
    // Get the role from the data - it could be a string or an object
    let role = data.role_name;
    if (typeof role === 'object' && role !== null) {
      role = role.value || role.label || role;
    }
    role = (role || '').toLowerCase();
    
    // Determine the primary role based on the role field first, then fall back to flags
    let primaryRole = role;
    if (!primaryRole || primaryRole === 'null' || primaryRole === 'undefined') {
      if (data.is_admin || data.isAdmin) primaryRole = 'admin';
      else if (data.is_teacher || data.isTeacher) primaryRole = 'teacher';
      else if (data.is_student || data.isStudent) primaryRole = 'student';
      else if (data.is_parent || data.isParent) primaryRole = 'parent';
      else primaryRole = 'student'; // default fallback
    }
    
    return {
      ...data,
      role: primaryRole,
      isAdmin: primaryRole === 'admin',
      isTeacher: primaryRole === 'teacher',
      isStudent: primaryRole === 'student',
      isParent: primaryRole === 'parent',
      is_admin: primaryRole === 'admin',
      is_teacher: primaryRole === 'teacher',
      is_student: primaryRole === 'student',
      is_parent: primaryRole === 'parent'
    };
  }, []);

  // Initialize auth state from storage
  const initializeAuth = useCallback(async () => {
    // Prevent multiple simultaneous initializations
    if (isInitializingRef.current) {
      return;
    }
    
    isInitializingRef.current = true;
    
    try {
      setLoading(true);
      
      const storedToken = localStorage.getItem('token') || sessionStorage.getItem('token');
      const storedUser = localStorage.getItem('user') || sessionStorage.getItem('user');
      
      if (!storedToken) {
        setUser(null);
        setToken(null);
        setLoading(false);
        return;
      }
      
      setToken(storedToken);
      
      // Use stored user data for immediate render
      if (storedUser) {
        try {
          const userData = JSON.parse(storedUser);
          setUser(processUserData(userData));
        } catch (e) {
          console.error('[Auth] Error parsing stored user data:', e);
        }
      }
      
      // Only verify token if we don't have user data or if user data is stale
      if (!storedUser || !JSON.parse(storedUser).id) {
        try {
          const response = await api.get(USER_PROFILE);
          if (response.data) {
            const userData = processUserData(response.data);
            setUser(userData);
            const storage = localStorage.getItem('token') ? localStorage : sessionStorage;
            storage.setItem('user', JSON.stringify(userData));
          }
        } catch (error) {
          console.error('[Auth] Error fetching user profile:', error);
          
          const status = error.response?.status;
          if (status === 401 || status === 403 || !status) {
            localStorage.removeItem('token');
            localStorage.removeItem('user');
            sessionStorage.removeItem('token');
            sessionStorage.removeItem('user');
            // API service will handle token removal automatically
            setUser(null);
            setToken(null);
            setLoading(false);
            navigate('/login', { replace: true });
          }
        }
      }
    } catch (error) {
      console.error('[Auth] Error initializing auth:', error);
    } finally {
      setLoading(false);
      isInitializingRef.current = false;
    }
  }, [navigate, processUserData]);

  // Run initialization on mount
  useEffect(() => {
    initializeAuth();
  }, [initializeAuth]);

  // Login function
  const login = useCallback(async (email, password, rememberMe = false) => {
    try {
      const response = await cleanAxios.post(AUTH_LOGIN, {
        email,
        password
      });
      
      // Check if the response indicates an error
      if (response.status >= 400) {
        if (response.status === 401) {
          const errorResult = {
            success: false,
            message: 'Invalid email or password.'
          };
          return errorResult;
        }
        
        if (response.status === 403) {
          const data = response.data || {};
          
          if (data.social_provider === 'google') {
            return {
              success: false,
              message: data.error || 'You registered using Google. Please sign in with Google.'
            };
          }

          if (data.error?.includes('verify your email')) {
            return {
              success: false,
              message: data.error || 'Please verify your email before logging in.',
              showResend: true
            };
          }

          if (data.error?.includes('pending admin approval')) {
            return {
              success: false,
              message: 'Activation awaited. Your account is pending admin approval.'
            };
          }

          if (data.requires_parent_consent) {
            return {
              success: false,
              message: data.error || 'We need parent/guardian consent before you can log in.',
              requiresParentConsent: true,
            };
          }

          if (data.error?.includes('inactive') || data.error?.includes('re-register') || data.error?.includes('subscription is inactive')) {
            return {
              success: false,
              message: data.error || 'Your subscription is inactive. Please re-register',
              redirect: data.redirect || '/get-started'
            };
          }

          return {
            success: false,
            message: data.error || 'Access denied. Please contact your administrator.'
          };
        }
        
        // Handle 409 CONFLICT (session conflict)
        if (response.status === 409) {
          const data = response.data || {};
          if (data.session_conflict) {
            return {
              success: false,
              sessionConflict: true,
              existingSessionIp: data.existing_session_ip,
              message: data.error || 'You are already logged in on another device.',
              email: email,
              allowKillSession: true,
              clearToken: data.clear_token
            };
          }
        }
        
        // Handle other error statuses
        return {
          success: false,
          message: response.data?.error || 'Login failed'
        };
      }
      
      const { token, user: userData } = response.data;
      
      if (!token || !userData) {
        throw new Error('Invalid response from server');
      }
      
      // Process and store user data
      const processedUser = processUserData(userData);
      
      // Store token and user data
      const storage = rememberMe ? localStorage : sessionStorage;
      storage.setItem('token', token);
      storage.setItem('user', JSON.stringify(processedUser));
      
      // Store email for future auto-login attempts
      localStorage.setItem('lastLoginEmail', email);
      
      // Set axios headers
      // API service will handle token automatically
      
      setUser(processedUser);
      setToken(token);
      
      return { success: true, user: processedUser };
      
    } catch (error) {
      console.error('[Auth] Login error:', error);
      
      // Handle any unexpected errors (network errors, etc.)
      return {
        success: false,
        message: error.message || 'Login failed. Please try again.'
      };
    } finally {
      setLoading(false);
    }
  }, [processUserData]);

  const requestLoginOtp = useCallback(async (email) => {
    const response = await cleanAxios.post(AUTH_LOGIN_REQUEST_OTP, { email });
    if (response.status >= 400) {
      throw new Error(response.data?.error || 'Failed to send login code.');
    }
    return response.data;
  }, []);

  const loginWithOtp = useCallback(async (email, otp, rememberMe = false) => {
    try {
      const response = await cleanAxios.post(AUTH_LOGIN_VERIFY_OTP, { email, otp });
      if (response.status >= 400) {
        const data = response.data || {};
        return {
          success: false,
          message: data.error || data.detail || 'Login failed',
          requiresParentConsent: !!data.requires_parent_consent,
        };
      }
      const { token, user: userData } = response.data;
      if (!token || !userData) {
        throw new Error('Invalid response from server');
      }
      const processedUser = processUserData(userData);
      const storage = rememberMe ? localStorage : sessionStorage;
      storage.setItem('token', token);
      storage.setItem('user', JSON.stringify(processedUser));
      localStorage.setItem('lastLoginEmail', email);
      setUser(processedUser);
      setToken(token);
      return { success: true, user: processedUser };
    } catch (error) {
      return {
        success: false,
        message: error.message || 'Login failed. Please try again.'
      };
    }
  }, [processUserData]);

  // Logout from other sessions
  const logoutOtherSessions = useCallback(async () => {
    try {
      const response = await api.post(AUTH_LOGOUT_OTHER_SESSIONS);
      
      return { 
        success: true, 
        message: response.data.message,
        deactivatedCount: response.data.deactivated_count
      };
      
    } catch (error) {
      console.error('[Auth] Error logging out other sessions:', error);
      throw new Error(error.response?.data?.error || 'Failed to logout from other sessions');
    }
  }, []);

  // Clear user sessions during login (for session conflict resolution)
  const clearUserSessions = useCallback(async (email) => {
    try {
              const response = await cleanAxios.post(CLEAR_USER_SESSIONS, {
          email: email
        });
      
      return { 
        success: true, 
        message: response.data.message,
        deactivatedCount: response.data.deactivated_count
      };
      
    } catch (error) {
      console.error('[Auth] Error clearing user sessions:', error);
      throw new Error(error.response?.data?.error || 'Failed to clear user sessions');
    }
  }, []);

  // Logout function
  const logout = useCallback(async () => {
    try {
      // Call logout endpoint first to clear server-side session
      try {
        await api.post(AUTH_LOGOUT);
      } catch (error) {
        // Continue with logout even if API call fails
      }
      
      // Clear auth data
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      sessionStorage.removeItem('token');
      sessionStorage.removeItem('user');
      // API service will handle token removal automatically
      
      setUser(null);
      setToken(null);
      setLoading(false);
      
      // Navigate to login page
      navigate('/login', { replace: true });
      
    } catch (error) {
      console.error('[Auth] Logout error:', error);
      // Even if API call fails, clear local state
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      sessionStorage.removeItem('token');
      sessionStorage.removeItem('user');
              // API service will handle token removal automatically
        setUser(null);
        setToken(null);
        setLoading(false);
        navigate('/login', { replace: true });
    }
  }, [navigate]);

  // Check if user is authenticated
  const isAuthenticated = useCallback(() => {
    const token = localStorage.getItem('token') || sessionStorage.getItem('token');
    
    if (!token) {
      return false;
    }
    
    // If we have a user in state, we're authenticated
    if (user && user.id) {
      return true;
    }
    
    // Check if we have stored user data
    const storedUser = localStorage.getItem('user') || sessionStorage.getItem('user');
    if (storedUser) {
      try {
        const userData = JSON.parse(storedUser);
        if (userData && userData.id) {
          setUser(processUserData(userData));
          return true;
        }
      } catch (e) {
        console.error('[Auth] Error parsing stored user data:', e);
      }
    }
    
    return false;
  }, [user, processUserData]);

  // Async function to verify authentication with server
  const verifyAuthentication = useCallback(async () => {
    const token = localStorage.getItem('token') || sessionStorage.getItem('token');
    
    if (!token) {
      return false;
    }
    
    // If we have a user in state, we're authenticated
    if (user && user.id) {
      return true;
    }
    
    // Check if we have stored user data
    const storedUser = localStorage.getItem('user') || sessionStorage.getItem('user');
    if (storedUser) {
      try {
        const userData = JSON.parse(storedUser);
        if (userData && userData.id) {
          setUser(processUserData(userData));
          return true;
        }
      } catch (e) {
        console.error('[Auth] Error parsing stored user data:', e);
      }
    }
    
    // Only make API call if we don't have stored user data
    try {
      const profileResponse = await api.get(USER_PROFILE);
      
      if (profileResponse?.data) {
        const userData = processUserData(profileResponse.data);
        setUser(userData);
        
        const storage = token === localStorage.getItem('token') ? localStorage : sessionStorage;
        storage.setItem('user', JSON.stringify(userData));
        
        return true;
      }
      
      return false;
    } catch (error) {
      console.error('[Auth] Error checking authentication:', error);
      
      if (error.response?.status === 401 || error.response?.status === 403) {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        sessionStorage.removeItem('token');
        sessionStorage.removeItem('user');
        // API service will handle token removal automatically
        setUser(null);
        setToken(null);
        setLoading(false);
        navigate('/login', { replace: true });
      }
      return false;
    }
  }, [navigate, processUserData, user]);

  // Check for existing session (no token issued; server uses its own IP for conflict detection)
  const checkExistingSession = useCallback(async (email) => {
    try {
      const response = await cleanAxios.post(SESSION_CHECK, { email });
      const data = response.data;

      if (response.status === 409 && data.session_conflict) {
        return {
          success: false,
          sessionConflict: true,
          existingSessionIp: data.existing_session_ip,
          message: data.message || 'You are already logged in on another device.',
          allowKillSession: true,
          clearToken: data.clear_token,
          email
        };
      }
      if (data.same_device) {
        return { success: true, sameDevice: true };
      }
      return { success: false, autoLogin: false };
    } catch (error) {
      console.error('[Auth] Error checking existing session:', error);
      return { success: false, autoLogin: false };
    }
  }, []);

  // Helper function to get client IP
  const getClientIP = async () => {
    try {
      // For local development, use localhost IP
      if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
        console.log('[Auth] Local development detected, using localhost IP');
        return '127.0.0.1';
      }
      
      // For production, try to get IP from a service
      const response = await fetch('https://api.ipify.org?format=json');
      const data = await response.json();
      console.log('[Auth] Client IP from external service:', data.ip);
      return data.ip;
    } catch (error) {
      console.warn('[Auth] Could not get client IP:', error);
      // Fallback to a default value or get from your backend
      return 'unknown';
    }
  };

  // Context value
  const value = {
    user,
    loading,
    login,
    requestLoginOtp,
    loginWithOtp,
    logout,
    logoutOtherSessions,
    clearUserSessions,
    checkExistingSession,
    initializeAuth,
    isAuthenticated,
    verifyAuthentication,
    setUser,
    showSnackbar,
    handleCloseSnackbar
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;
